'use client';

import { type CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SoundSystem } from '../hooks/useSoundSystem';

interface Props {
  sound?: SoundSystem;
  stopAnimation: boolean;
  onStopAnimationToggle: () => void;
}

export default function SoundSettings({ sound, stopAnimation, onStopAnimationToggle }: Props) {
  const { t } = useTranslation();
  const [showSettings, setShowSettings] = useState(false);

  // Close panel on outside click
  useEffect(() => {
    if (!showSettings) return;
    const close = () => { setShowSettings(false); };
    document.addEventListener('pointerdown', close);
    return () => { document.removeEventListener('pointerdown', close); };
  }, [showSettings]);

  return (
    <div style={{ position: 'relative', zIndex: 50 }}>
      {/* Settings gear button */}
      <button
        type="button"
        onPointerDown={(e) => { e.stopPropagation(); }}
        onClick={() => { setShowSettings((v) => !v); }}
        aria-label={t('settings.soundSettings')}
        className={`settings-toggle ${showSettings ? 'settings-toggle--open' : ''}`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {showSettings && (
        <div
          onPointerDown={(e) => { e.stopPropagation(); }}
          className="settings-panel"
        >
          <div className="settings-panel__ring" />
          <div className="settings-panel__title">{t('settings.soundSettings')}</div>

          {/* Music + SFX — only when a sound system is available */}
          {sound && (
            <>
              <div className="settings-panel__group">
                <div className="settings-panel__row">
                  <span className="settings-panel__label">{t('settings.music')}</span>
                  <span className="settings-panel__value">
                    {sound.musicMultiplier === 0 ? t('settings.off') : `${String(Math.round(sound.musicMultiplier * 100))}%`}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={sound.musicMultiplier}
                  onChange={(e) => { sound.setMusicMultiplier(parseFloat(e.target.value)); }}
                  className="gold-range"
                  style={{ '--range-progress': `${String(Math.round(sound.musicMultiplier * 100))}%` } as CSSProperties}
                />
              </div>

              <div className="settings-panel__group">
                <div className="settings-panel__row">
                  <span className="settings-panel__label">{t('settings.effects')}</span>
                  <span className="settings-panel__value">
                    {sound.sfxMultiplier === 0 ? t('settings.off') : `${String(Math.round(sound.sfxMultiplier * 100))}%`}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={sound.sfxMultiplier}
                  onChange={(e) => { sound.setSfxMultiplier(parseFloat(e.target.value)); }}
                  className="gold-range"
                  style={{ '--range-progress': `${String(Math.round(sound.sfxMultiplier * 100))}%` } as CSSProperties}
                />
              </div>
            </>
          )}

          {/* Disable Animations */}
          <div className="settings-panel__group" style={{ marginBottom: 0 }}>
            <div className="settings-panel__row">
              <span className="settings-panel__label">{t('settings.disableAnimations')}</span>
              <button
                type="button"
                onClick={onStopAnimationToggle}
                className={`flex h-6 w-11 shrink-0 items-center rounded-full p-1 transition ${stopAnimation ? 'justify-end bg-[rgba(var(--game-accent-rgb),_0.4)]' : 'justify-start bg-white/10'}`}
              >
                <span className="h-4 w-4 rounded-full bg-white" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
